/**
 * Pure (non-hook) utilities for postMessage communication with the parent Ionic app.
 * This module is intentionally framework-free so it can be imported by the API client
 * class as well as by React hooks/components.
 *
 * See frontend/PARENT_APP_IFRAME.md for the full postMessage contract.
 */

export const PARENT_APP_ORIGIN = import.meta.env.VITE_PARENT_APP_ORIGIN ?? '*';

const TOKEN_REFRESH_TIMEOUT_MS = 5_000;

const ALLOWED_ORIGINS: string[] = [
  'capacitor://localhost',
  'http://localhost',
  'https://localhost',
];
if (globalThis.location?.origin) {
  ALLOWED_ORIGINS.push(globalThis.location.origin);
}

function isAllowedOrigin(origin: string): boolean {
  // Accept if no origin (internal communication)
  if (!origin || origin === 'null') return true;
  return ALLOWED_ORIGINS.some(allowed => origin.startsWith(allowed));
}

export function detectIframe(): boolean {
  try {
    return (globalThis as unknown as Window) !== globalThis.top;
  } catch {
    // Cross-origin iframes throw a SecurityError when accessing globalThis.top
    return true;
  }
}

/**
 * Requests a fresh JWT from the parent app via postMessage.
 * Sends `REQUEST_TOKEN_REFRESH` and waits for `TOKEN_REFRESH_RESPONSE`.
 * Rejects with a timeout error after 5 seconds.
 *
 * Must only be called when `detectIframe()` returns true.
 */
export function requestTokenFromParent(): Promise<string> {
  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      globalThis.removeEventListener('message', handleResponse);
    };

    const handleResponse = (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) {
        console.warn('Message received from an unauthorized origin:', event.origin);
        return;
      }

      if (event.data?.type === 'TOKEN_REFRESH_RESPONSE') {
        if (settled) return;
        settled = true;
        cleanup();
        const token: unknown = event.data.token;
        if (typeof token === 'string' && token.length > 0) {
          resolve(token);
        } else {
          reject(new Error('Parent app returned an empty or invalid token'));
        }
      }
    };

    globalThis.addEventListener('message', handleResponse);

    // The parent app contract requires '*' as target origin for token refresh
    window.parent.postMessage({ type: 'REQUEST_TOKEN_REFRESH' }, '*');

    setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Token refresh timeout: parent app did not respond within 5 s'));
    }, TOKEN_REFRESH_TIMEOUT_MS);
  });
}
